import React, { useEffect, useState } from "react";
import TextField from "../../../components/inputs/textfield";
import Snackbar from "../../../components/snackbar/snackbar";
import {
  admin_challenge_opportunity_update,
  get_challenge_opportunity_list,
  get_market_analysis_list,
} from "../admin.api";
import { useParams } from "react-router-dom";

import "./view.css";
import { FormValidate } from "../../../components/formValidate";
import Loader from "../../../components/loader/loader";

let required = [
  "market_analysis_opportunity_id",
  "title",
  "description",
  "due_date",
];

export default function EditChallengeOpportunity(props) {
  const [basicData, setBasicData] = useState({});
  const [marketOpportunities, setMarketOpportunities] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const selectedParam = useParams();

  const toYyyyMmDd = (ddMmYyyy) => {
    if (!ddMmYyyy) return "";
    const [day, month, year] = String(ddMmYyyy).split("-");
    if (!year || !month || !day) return "";
    return `${year}-${month}-${day}`;
  };

  const toDdMmYyyy = (yyyyMmDd) => {
    if (!yyyyMmDd) return "";
    const [year, month, day] = String(yyyyMmDd).split("-");
    if (!year || !month || !day) return "";
    return `${day}-${month}-${year}`;
  };

  const hydrate = (row) => {
    if (!row) return;
    setBasicData({
      ...row,
      market_analysis_opportunity_id:
        row?.market_analysis_opportunity_id ??
        row?.market_analysis_opportunity?.id ??
        "",
      due_date: toYyyyMmDd(row?.due_date),
    });
  };

  const loadInitial = async () => {
    if (!selectedParam?.id) return;
    try {
      setIsActive(true);
      const res = await get_challenge_opportunity_list();
      const found = Array.isArray(res)
        ? res.find((x) => String(x.id) === String(selectedParam.id))
        : null;
      hydrate(found);
    } finally {
      setIsActive(false);
    }
  };

  const loadMarketAnalysisOpportunities = async () => {
    try {
      const res = await get_market_analysis_list();
      setMarketOpportunities(Array.isArray(res) ? res : []);
    } catch {
      setMarketOpportunities([]);
    }
  };

  useEffect(() => {
    loadInitial();
    loadMarketAnalysisOpportunities();
  }, [selectedParam]);

  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const handleChange = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    let data = basicData;
    data = {
      ...data,

      [name]: value,
    };
    setBasicData(data);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];

    if (file) {
      const limitMb = name === "image" ? 2 : 5;
      const maxSize = limitMb * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackMessage(`File size must be less than ${limitMb}MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        setIsSnackError(true);
        setSnackOpen(true);
        // Clear the file input
        e.target.value = '';
        return;
      }
    }

    setBasicData((prev) => ({ ...prev, [name]: file }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = FormValidate(basicData, required);
    setIsActive(true);
    if (Object.keys(errors).length === 0) {
      let id = selectedParam?.id || basicData?.id;
      const form = new FormData();
      form.append(
        "market_analysis_opportunity_id",
        basicData?.market_analysis_opportunity_id
      );
      form.append("title", basicData?.title);
      form.append("description", basicData?.description);
      form.append("site_url", basicData?.site_url);
      form.append("due_date", toDdMmYyyy(basicData?.due_date));
      if (basicData?.brochure instanceof File) {
        form.append("brochure", basicData?.brochure);
      }
      if (basicData?.image instanceof File) {
        form.append("image", basicData?.image);
      }

      admin_challenge_opportunity_update(id, form)
        .then((res) => {
          setSnackMessage(res.message);
          setIsSnackError(false);
          setSnackOpen(true);
        })
        .catch((error) => {
          setSnackMessage(error.message);
          setIsSnackError(true);
          setSnackOpen(true);
        });
    } else {
      setSnackMessage("Please fill all mandatory fields");
      setIsSnackError(true);
      setSnackOpen(true);
      setIsActive(false);
    }
  };

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!flag) {
      props.history.push("/challenges");
    }
  };

  return (
    <div className="add-per">
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
      <Loader open={isActive} />
      <div className="view-container">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 pt-2">
            <b>Edit Challenge Opportunity</b>
          </h6>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={(e) => {
              props.history.push("/challenges");
            }}
          >
            Back
          </button>
        </div>
        <hr />

        <div className="row">
          <div className="col-12">
            <label className="control-label mylabel" htmlFor="market_analysis_opportunity_id">
              Market Analysis Opportunity
              <span className="required-asterisk">*</span>
            </label>
            <div className="mandatory input-group">
              <select
                className="form-control mb-2"
                name="market_analysis_opportunity_id"
                id="market_analysis_opportunity_id"
                value={basicData?.market_analysis_opportunity_id ?? ""}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {marketOpportunities.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.opportunity}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-12">
            <TextField
              fullwidth
              label="Title"
              required
              value={basicData?.title ?? ""}
              onChange={handleChange}
              name="title"
              id="title"
              aria-describedby="emailHelp"
            />
          </div>
          <div className="col-12">
            <TextField
              fullwidth
              label="Description"
              required
              multiline
              value={basicData?.description ?? ""}
              onChange={handleChange}
              name="description"
              id="description"
            />
          </div>
          <div className="col-12">
            <TextField
              fullwidth
              label="Site URL"
              value={basicData?.site_url ?? ""}
              onChange={handleChange}
              name="site_url"
              id="site_url"
            />
          </div>

          <div className="col-12">
            <TextField
              fullwidth
              label="Due Date"
              required
              value={basicData?.due_date ?? ""}
              onChange={handleChange}
              name="due_date"
              id="due_date"
              type="date"
              min={new Date().toISOString().split('T')[0]}

            />
          </div>

          <div className="col-12">
            <TextField
              label="Reference Document (Max Size: 5MB)"
              id="brochure"
              onChange={handleFileChange}
              accept=".pdf"
              fileName={
                basicData?.brochure instanceof File
                  ? basicData?.brochure?.name
                  : basicData?.brochure
              }
              name="brochure"
              type="file"
            />
          </div>

          <div className="col-12">
            <TextField
              label="Image (Max Size: 2MB)"
              id="image"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png"
              fileName={
                basicData?.image instanceof File
                  ? basicData?.image?.name
                  : basicData?.image
              }
              name="image"
              type="file"
            />
          </div>
          <div className="col-12">
            <center>
              <button
                type="button"
                className="btn btn-dark mt-3"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </center>
          </div>

          {/* ---------------- */}
        </div>
      </div>
    </div>
  );
}
